/**
 * Metadata shapes shared by every writer of a given audit action, so the REST
 * route and the copilot executor (INVARIANTS §3.6) cannot drift apart.
 */
// A `type`, not an `interface`: Prisma's InputJsonValue needs an index-signature-compatible
// shape, and an interface is not assignable to one.
export type EnrollmentCreatedMetadata = {
  studentId: string;
  subjectId: string;
  pricingType: string;
  billingPeriod: string;
  ratePerSession: number | null;
  monthlyRate: number | null;
  termSize: number | null;
  chargeAbsent: boolean;
  /** Which write path produced the row; absent means the REST route. */
  source?: 'copilot';
};

/** What ENROLLMENT_CREATED remembers: the money-shaping fields at birth, so a
 *  later RATE_CHANGED / CHARGE_ABSENT_CHANGED row has a known starting point.
 *  Decimal columns arrive as Prisma Decimals; they are stored as numbers. */
export function enrollmentCreatedMetadata(
  e: {
    studentId: string; subjectId: string; pricingType: string; billingPeriod: string;
    ratePerSession: unknown; monthlyRate: unknown; termSize: number | null; chargeAbsent: boolean;
  },
  source?: 'copilot',
): EnrollmentCreatedMetadata {
  return {
    studentId: e.studentId,
    subjectId: e.subjectId,
    pricingType: e.pricingType,
    billingPeriod: e.billingPeriod,
    ratePerSession: e.ratePerSession == null ? null : Number(e.ratePerSession),
    monthlyRate: e.monthlyRate == null ? null : Number(e.monthlyRate),
    termSize: e.termSize,
    chargeAbsent: e.chargeAbsent,
    ...(source ? { source } : {}),
  };
}

// ── THI-259: the org's own view of its AuditLog ─────────────────────────────

/** The buckets the "Nhật ký thao tác" page filters by. `clients` is the mirrored
 *  customer timeline (THI-260); `other` is every action not mapped below, so a
 *  new action is visible before it is labelled. */
export const AUDIT_GROUPS = ['money', 'students', 'clients', 'org', 'platform', 'other'] as const;
export type AuditGroup = (typeof AUDIT_GROUPS)[number];

/** One map, used by the API filter (`action IN (...)`) and by the FE icon, so
 *  they cannot disagree. Platform-level rows with a null organizationId
 *  (promotions, platform config) never reach an org page and are not listed. */
export const AUDIT_ACTION_GROUPS: Record<string, Exclude<AuditGroup, 'other'>> = {
  MONTH_CLOSED: 'money',
  INVOICE_UPDATED: 'money',
  PAYMENT_RECORDED: 'money',
  PAYMENT_LINKED: 'money',
  PAYMENT_IGNORED: 'money',
  PAYMENT_DELETED: 'money',
  INVOICE_DUE_DATE_BULK: 'money',
  EXPENSE_CREATED: 'money',
  EXPENSE_UPDATED: 'money',
  EXPENSE_DELETED: 'money',
  PAYROLL_ADJUSTMENT_CREATED: 'money',
  PAYROLL_ADJUSTMENT_DELETED: 'money',
  INVOICE_DELETED: 'money',
  INVOICE_CLIENT_CHANGED: 'money',
  MONTH_REOPENED: 'money',
  PAYROLL_FINALIZED: 'money',
  PAYROLL_REOPENED: 'money',
  PAYER_REASSIGNED: 'money',
  PROMOTION_APPLIED: 'money',
  SEPAY_COMPANY_REMAPPED: 'money',
  SEPAY_COMPANY_STATUS_CHANGED: 'money',
  SEPAY_DEPOSIT_ADJUSTED: 'money',
  SEPAY_LINK_TOKEN_REVOKED: 'money',
  SEPAY_UNLINK_STARTED: 'money',
  ENROLLMENT_CREATED: 'students',
  ENROLLMENT_PAUSED: 'students',
  ENROLLMENT_RESUMED: 'students',
  ENROLLMENT_ENDED: 'students',
  ENROLLMENT_RATE_CHANGED: 'students',
  ENROLLMENT_DELETED: 'students',
  ENROLLMENT_CHARGE_ABSENT_CHANGED: 'students',
  ENROLLMENT_CHARGE_ABSENT_BULK: 'students',
  STUDENT_TAGS_CHANGED: 'students',
  STUDENT_DEACTIVATED: 'students',
  STUDENT_REACTIVATED: 'students',
  STUDENT_DELETED: 'students',
  STUDENT_PROGRESS_LINK_BULK_CREATED: 'students',
  STUDENT_PROGRESS_LINK_CREATED: 'students',
  STUDENT_PROGRESS_LINK_ROTATED: 'students',
  SUBJECT_NOTE_TEMPLATE_UPDATED: 'students',
  SUBJECT_NOTE_TEMPLATE_REMOVED: 'students',
  SLOT_MEMBER_MOVED: 'students',
  SLOT_CREATED: 'students',
  SLOT_UPDATED: 'students',
  SLOT_ARCHIVED: 'students',
  SLOT_UNARCHIVED: 'students',
  SLOT_MEMBER_ADDED: 'students',
  SLOT_MEMBER_REMOVED: 'students',
  SESSION_CREATED: 'students',
  SESSION_UPDATED: 'students',
  SESSION_CANCELLED: 'students',
  SESSION_PARTICIPANT_ADDED: 'students',
  SESSION_PARTICIPANT_REMOVED: 'students',
  SESSION_PARTICIPANT_MOVED: 'students',
  SCHEDULE_TEMPLATE_APPLIED: 'students',
  STUDENT_CREATED: 'students',
  STUDENT_UPDATED: 'students',
  STUDENTS_IMPORTED: 'students',
  SUBJECT_CREATED: 'students',
  SUBJECT_UPDATED: 'students',
  SUBJECT_DELETED: 'students',
  SCHEDULE_TEMPLATE_CREATED: 'students',
  SCHEDULE_TEMPLATE_UPDATED: 'students',
  SCHEDULE_TEMPLATE_DELETED: 'students',
  SCHEDULE_TEMPLATE_ENTRIES_REPLACED: 'students',
  // THI-267: the class layer (ClassGroup). entityType CLASS. Not yet
  // written by any route — Task 4/6 are the first callers.
  CLASS_CREATED: 'students',
  CLASS_UPDATED: 'students',
  CLASS_ARCHIVED: 'students',
  CLASS_UNARCHIVED: 'students',
  CLASS_MEMBERS_ADDED: 'students',
  CLIENT_INVOICE_CREATED: 'clients',
  CLIENT_INVOICE_SENT: 'clients',
  CLIENT_INVOICE_PAID: 'clients',
  CLIENT_INVOICE_OVERDUE: 'clients',
  CLIENT_INVOICE_CANCELLED: 'clients',
  CLIENT_INVOICE_VIEWED: 'clients',
  CLIENT_REMINDER_SENT: 'clients',
  CLIENT_NOTE_ADDED: 'clients',
  CLIENT_NOTE_EDITED: 'clients',
  CLIENT_TAG_ADDED: 'clients',
  CLIENT_TAG_REMOVED: 'clients',
  CLIENT_INFO_UPDATED: 'clients',
  CLIENT_CREATED: 'clients',
  CLIENT_DELETED: 'clients',
  CLIENT_NOTE_DELETED: 'clients',
  ORG_INFO_UPDATED: 'org',
  ORG_SETTINGS_UPDATED: 'org',
  ORG_PREFERENCES_UPDATED: 'org',
  MEMBER_INVITED: 'org',
  MEMBER_ROLE_CHANGED: 'org',
  MEMBER_REMOVED: 'org',
  MEMBER_PLACEHOLDER_CREATED: 'org',
  MEMBER_CONVERTED: 'org',
  BANK_LINKED: 'org',
  BANK_UNLINKED: 'org',
  BANK_PRIMARY_CHANGED: 'org',
  BRANCH_CREATED: 'org',
  BRANCH_UPDATED: 'org',
  BRANCH_ARCHIVED: 'org',
  BRANCH_UNARCHIVED: 'org',
  BRANCH_SLOTS_ASSIGNED: 'org',
  SUBSCRIPTION_UPGRADE_REQUESTED: 'org',
  SUBSCRIPTION_RENEWAL_STOPPED: 'org',
  SUBSCRIPTION_RENEWAL_RESUMED: 'org',
  SUBSCRIPTION_INVOICE_CANCELLED: 'org',
  INVITATION_REVOKED: 'org',
  PLAN_ASSIGNED: 'org',
  ORG_SUSPENDED: 'org',
  ORG_UNSUSPENDED: 'org',
  DEMO_ORG_RESET: 'org',
  USER_IMPERSONATION_STARTED: 'platform',
  USER_IMPERSONATION_REDEEMED: 'platform',
  USER_EMAIL_CHANGED: 'platform',
  USER_EMAIL_VERIFIED: 'platform',
  USER_RESET_LINK_SENT: 'platform',
};

export function auditGroupOf(action: string): AuditGroup {
  return AUDIT_ACTION_GROUPS[action] ?? 'other';
}

export function auditActionsInGroup(group: Exclude<AuditGroup, 'other'>): string[] {
  return Object.entries(AUDIT_ACTION_GROUPS).filter(([, g]) => g === group).map(([a]) => a);
}

/** Rows the org's own page must never list, whatever the filter: everything
 *  LopHoc support did on the org's user accounts, impersonation first of all.
 *  Johnny, 2026-09-08: impersonation is read-only and changes nothing of the
 *  org's, so the org has nothing to audit there; support must be incognito
 *  to the customer. The admin feed still shows every row. Actions LopHoc
 *  takes ON THE ORG (plan assigned, suspended, demo reset) stay visible with
 *  the anonymous "LopHoc" actor: those change what the org sees. */
export const ORG_HIDDEN_AUDIT_ACTIONS: readonly string[] = auditActionsInGroup('platform');

/** The groups the org page offers as a filter: `platform` is entirely hidden. */
export const ORG_AUDIT_GROUPS = AUDIT_GROUPS.filter((g) => g !== 'platform');

/** Who did it, as the org is allowed to see it: a member by name, LopHoc
 *  staff as "platform" with no identity (the org learns THAT the platform
 *  acted, not which employee), or the system (cron, reconciliation). */
export interface OrgAuditActor {
  id: string | null;
  name: string | null;
  kind: 'member' | 'platform' | 'system';
}

/** A click-through the API resolved because the target still exists. */
export interface OrgAuditLink {
  type: 'invoice' | 'student' | 'client' | 'subject';
  id: string;
}

export interface OrgAuditRow {
  id: string;
  action: string;
  group: AuditGroup;
  entityType: string;
  entityId: string;
  metadata: unknown;
  actor: OrgAuditActor;
  link: OrgAuditLink | null;
  createdAt: string;
}

/** `GET /audit/actors`: the member actors seen in this org's rows, plus
 *  whether platform or system rows exist, so the actor filter only offers
 *  options that return something. */
export interface OrgAuditActors {
  members: { id: string; name: string | null }[];
  hasPlatform: boolean;
  hasSystem: boolean;
}
